/**
 * UL-8133 -- what the Impressions target cell makes of what was typed into it.
 *
 * Blank clears the target (null). Anything else must be a number of zero or more with at most 2
 * decimal places; thousands separators are allowed (`1,000,000.50`) but must be well placed. A
 * negative, text, an exponent or a third decimal is refused in the cell and never sent.
 */
export type ImpressionsTargetInput = {
    /** The target to save: null to clear it. NaN when the text is not acceptable. */
    parsed: number | null;
    invalid: boolean;
    unchanged: boolean;
};

const PLAIN = /^\d+(\.\d{1,2})?$/;
const GROUPED = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;

export const parseImpressionsTargetInput = (
    value: string | number,
    current: number | null,
): ImpressionsTargetInput => {
    const trimmed = String(value).trim();

    if (trimmed === '') {
        return { parsed: null, invalid: false, unchanged: current === null };
    }

    if (!PLAIN.test(trimmed) && !GROUPED.test(trimmed)) {
        return { parsed: Number.NaN, invalid: true, unchanged: false };
    }

    const parsed = Number(trimmed.replace(/,/g, ''));

    return { parsed, invalid: false, unchanged: current !== null && parsed === Number(current) };
};

const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

/** Thousands separators, decimals only when there are any; '' for no target. */
export const formatImpressionsTarget = (value: number | null | undefined): string =>
    value == null || Number.isNaN(Number(value)) ? '' : formatter.format(Number(value));
